<template>
	<div class="navbar">
		<hamburger id="hamburger-container" :is-active="sidebar.opened" class="hamburger-container" @toggleClick="toggleSideBar" />

		<!-- <breadcrumb id="breadcrumb-container" class="breadcrumb-container" /> -->

		<div class="right-menu">
			<template v-if="device !== 'mobile'">
				<template v-if="env !== 'development' && env !== 'production' && env !== 'staging'">
					<search id="header-search" class="right-menu-item" />

					<error-log class="errLog-container right-menu-item hover-effect" />

					<screenfull id="screenfull" class="right-menu-item hover-effect" />

					<el-tooltip content="Global Size" effect="dark" placement="bottom">
						<size-select id="size-select" class="right-menu-item hover-effect" />
					</el-tooltip>
				</template>
			</template>

			<div class="right-menu-item">
				<el-select
					:value="selectedLegalEntity"
					size="mini"
					style="width: 200px;"
					filterable
					@change="handleChange"
				>
					<el-option v-for="item in legalEntities" :key="item.key" :label="(hasMultipleParticipants ? item.participant.name + ' - ' : '') + (item.legal_entity ? (item.legal_entity.display_name || item.legal_entity.registration_name) : $t('general.all_legal_entities'))" :value="item.key">
						<template v-if="hasMultipleParticipants" >
							<span style="float: left">{{ item.participant.name }}</span>
							<span style="float: right; color: #8492a6; font-size: 13px">{{ item.legal_entity ? (item.legal_entity.display_name || item.legal_entity.registration_name) : $t('general.all_legal_entities') }}</span>
						</template>
						<template v-else>
							<span>{{ item.legal_entity ? (item.legal_entity.display_name || item.legal_entity.registration_name) : $t('general.all_legal_entities') }}</span>
						</template>
					</el-option>
				</el-select>
			</div>
			<lang-select class="right-menu-item hover-effect" />

			<el-dropdown class="avatar-container right-menu-item hover-effect" trigger="click">
				<div class="avatar-wrapper">
					<img src="@/assets/risolto_logo.png" class="user-avatar" style="vertical-align: middle;" />
					<i class="el-icon-caret-bottom" />
				</div>
				<el-dropdown-menu slot="dropdown">
					<router-link to="/profile/index">
						<el-dropdown-item>Account info</el-dropdown-item>
					</router-link>
					<el-dropdown-item v-if="isLoggedIn" divided>
						<span style="display:block;" @click="logout">Log Out</span>
					</el-dropdown-item>
					<el-dropdown-item v-else divided>
						<span style="display:block;" @click="login">Log In</span>
					</el-dropdown-item>
				</el-dropdown-menu>
			</el-dropdown>
		</div>
	</div>
</template>

<script>
import { determineLoggedInStatus } from '@/utils/auth';
import { mapGetters } from 'vuex';
import Hamburger from '@/components/Hamburger';
import ErrorLog from '@/components/ErrorLog';
import Screenfull from '@/components/Screenfull';
import LangSelect from '@/components/LangSelect';
import SizeSelect from '@/components/SizeSelect';
import Search from '@/components/HeaderSearch';
import { gql } from 'graphql-tag';

import cognito from '@/cognito';

export default {
	components: {
		Hamburger,
		ErrorLog,
		Screenfull,
		LangSelect,
		Search,
		SizeSelect
	},
	data() {
		return {
			currentTime: null
		};
	},
	computed: {
		...mapGetters(['sidebar', 'avatar', 'device', 'token', 'env', 'roles', 'loggedInUser', 'legalEntities', 'selectedParticipantStore', 'selectedLegalEntityStore']),
		isLoggedIn() {
			return determineLoggedInStatus();
		},
		hasMultipleParticipants() {
			const participants = this.legalEntities.map(p => p.participant.reference);
			const uniqueParticipants = [...new Set(participants)];

			return uniqueParticipants.length > 1;
		},
		selectedLegalEntity() {
			let value = this.selectedParticipantStore;

			if (this.selectedLegalEntityStore != null) {
				value += '.' + this.selectedLegalEntityStore;
			}

			return value;
		}
	},
	watch: {
		'$store.state.apollo.risolto_message': function() {
			this.fetchUnreadMessagesCount();
		}
	},
	methods: {
		handleChange(value) {
			// split value by - to get participant and legal entity
			value = value.split('.');
			const participant = value[0];
			const legalEntity = value[1];

			this.$store.commit('risolto/SET_SELECTED_PARTICIPANT', participant);
			if (legalEntity !== null) {
				this.$store.commit('risolto/SET_SELECTED_LEGAL_ENTITY', legalEntity);
			} else {
				this.$store.commit('risolto/SET_SELECTED_LEGAL_ENTITY', null);
			}

			// Get the current URL path
			const currentPath = this.$route.path;

			// Check if there are extra parts to the URL after the participant ID
			const pathParts = currentPath.split('/');

			// The first part should always be the participant ID
			// Let's handle the case where we only need the participant and nothing else
			const newPath = `/${participant}`;

			// Check if there's something after the participant ID that needs to be preserved (e.g. /recipients, /messages)
			if (pathParts.length > 2) {
				// If there's more in the URL after the participant, check what it is
				const remainingPath = `${pathParts.slice(2)[0]}/index`; // Get the remaining path after the participant ID
				this.$router.replace({path: `/${participant}/${remainingPath}`, replace: true});
			} else {
				// If there's nothing more in the URL after the participant, just use the new participant path
				this.$router.replace({path: newPath, replace: true});
			}
		},
		toggleSideBar() {
			this.$store.dispatch('app/toggleSideBar');
		},
		login() {
			this.$router.push({ path: '/login' });
		},
		async logout() {
			cognito.logout();
		},
		async fetchUnreadMessagesCount() {
			this.$apollo.query({
				query: gql`
				query componentsNavbar__messages_incoming(
					$participant: uuid!
				) {
				risolto_message(
					where: {
						participant: { _eq: $participant }
						archived_at: { _is_null: true }
					}
				) {
					reference
				}
			}
			`,
				variables: {
					participant: this.$store.getters.selectedParticipantStore
				}
			}).then(({ data }) => {
				this.$store.commit('messages/SET_UNREAD_COUNT', data.risolto_message.length);
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.navbar {
	height: 50px;
	overflow: hidden;
	position: relative;
	background: #fff;
	box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

	.hamburger-container {
		line-height: 46px;
		height: 100%;
		float: left;
		cursor: pointer;
		transition: background 0.3s;
		-webkit-tap-highlight-color: transparent;

		&:hover {
			background: rgba(0, 0, 0, 0.025);
		}
	}

	.breadcrumb-container {
		float: left;
	}

	.errLog-container {
		display: inline-block;
		vertical-align: top;
	}

	.right-menu {
		float: right;
		height: 100%;
		line-height: 50px;

		&:focus {
			outline: none;
		}

		.right-menu-item {
			display: inline-block;
			padding: 0 8px;
			height: 100%;
			// font-size: 18px;
			color: #5a5e66;
			vertical-align: text-bottom;

			&.hover-effect {
				cursor: pointer;
				transition: background 0.3s;

				&:hover {
					background: rgba(0, 0, 0, 0.025);
				}
			}
		}

		.avatar-container {
			margin-right: 30px;

			.avatar-wrapper {
				position: relative;

				.user-avatar {
					cursor: pointer;
					width: 40px;
					height: 40px;
					//   border-radius: 10px;
				}

				.el-icon-caret-bottom {
					cursor: pointer;
					position: absolute;
					right: -20px;
					top: 25px;
					font-size: 12px;
				}
			}
		}
	}
}
</style>
